/**
 * Mega Menu — far-right preview image (Sea-to-Summit style)
 * Each mega menu list (.mega-menu__list) has ONE large preview image pinned to the
 * far right (.mega-menu__preview-img). Hovering any product/subcategory link swaps the
 * preview to that item's image; leaving the list resets it to the default.
 */

(function () {
  'use strict';

  function initPreviewSwitch() {
    const lists = document.querySelectorAll('.mega-menu__list');

    lists.forEach((list) => {
      const menu = list.closest('.mega-menu');
      if (menu && menu.dataset.hoverSwitch === 'false') return;

      const previewImg = list.querySelector('.mega-menu__preview-img');
      if (!previewImg) return;
      if (list.dataset.previewBound === 'true') return; // guard against double-binding
      list.dataset.previewBound = 'true';

      const previewDesc = list.querySelector('[data-preview-desc]');
      const previewSpecs = {
        wrap: list.querySelector('[data-preview-specs]'),
        dims: list.querySelector('[data-preview-dims]'),
        dimsVal: list.querySelector('[data-preview-dims-val]'),
        weight: list.querySelector('[data-preview-weight]'),
        weightVal: list.querySelector('[data-preview-weight-val]'),
      };
      const defaultSrc = previewImg.dataset.previewDefault || previewImg.src;
      // Bind to links that swap the image OR carry a short description.
      const links = list.querySelectorAll('[data-hover-image], [data-hover-desc], [data-hover-dims], [data-hover-weight]');
      if (links.length === 0) return;

      let currentSrc = defaultSrc;
      let swapToken = 0;

      /* The preview is a single <img> whose src we rewrite, and a browser keeps painting the
         OLD pixels until the new file has decoded. Assigning src and un-hiding the image on a
         fixed timer therefore faded it back in still showing the previous product's photo —
         which is exactly what it looked like. Fetch and decode off-screen first, swap after. */
      const cache = new Map();
      const preload = (src) => {
        let img = cache.get(src);
        if (!img) {
          img = new Image();
          img.src = src;
          cache.set(src, img);
        }
        if (img.decode) return img.decode().catch(() => {});
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      };

      // A slow or missing file must not leave the panel blanked out for good.
      const atMost = (promise, ms) =>
        Promise.race([promise, new Promise((resolve) => window.setTimeout(resolve, ms))]);

      const swapTo = (src) => {
        if (!src || src === currentSrc) return;
        currentSrc = src;
        const token = ++swapToken;

        previewImg.classList.add('is-switching');
        // The fade-out runs while the file downloads; the swap waits for both.
        const faded = new Promise((resolve) => window.setTimeout(resolve, 120));

        Promise.all([atMost(preload(src), 1200), faded]).then(() => {
          if (token !== swapToken) return; // pointer moved on, a newer swap owns the image now
          previewImg.src = src;
          previewImg.classList.remove('is-switching');
        });
      };

      /* Warm the whole panel the first time it is opened. Without this every product pays for
         its own round trip on first hover, because data-hover-image is invisible to the
         browser's preload scanner. */
      let warmed = false;
      const warm = () => {
        if (warmed) return;
        warmed = true;
        links.forEach((link) => {
          if (link.dataset.hoverImage) preload(link.dataset.hoverImage);
        });
      };
      const warmRoot = menu || list;
      [warmRoot, warmRoot.closest('li')].forEach((el) => {
        if (!el) return;
        el.addEventListener('pointerenter', warm, { once: true });
        el.addEventListener('focusin', warm, { once: true });
      });

      const setDesc = (text) => {
        if (!previewDesc) return;
        previewDesc.textContent = text || '';
        previewDesc.classList.toggle('is-visible', !!text);
      };

      // Each row hides on its own, and the whole block hides when neither has a value, so a
      // product without measurements shows no dangling label.
      const setSpecs = (dims, weight) => {
        if (!previewSpecs.wrap) return;
        if (previewSpecs.dims) {
          if (previewSpecs.dimsVal) previewSpecs.dimsVal.textContent = dims || '';
          previewSpecs.dims.hidden = !dims;
        }
        if (previewSpecs.weight) {
          if (previewSpecs.weightVal) previewSpecs.weightVal.textContent = weight || '';
          previewSpecs.weight.hidden = !weight;
        }
        previewSpecs.wrap.classList.toggle('is-visible', !!(dims || weight));
      };

      links.forEach((link) => {
        const enter = () => {
          swapTo(link.dataset.hoverImage);
          setDesc(link.dataset.hoverDesc);
          setSpecs(link.dataset.hoverDims, link.dataset.hoverWeight);
        };
        link.addEventListener('mouseenter', enter);
        // keyboard parity: focusing a link previews it too
        link.addEventListener('focus', enter);
      });

      // reset to default when leaving the whole list
      list.addEventListener('mouseleave', () => {
        swapTo(defaultSrc);
        setDesc('');
        setSpecs('', '');
      });
    });
  }

  function init() {
    initPreviewSwitch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
